<script setup lang="ts">
/**
 * ConnectIntegrationModal — подключение внешней интеграции (inbox-188).
 *
 * 1:1 порт docs/mockups/modal-connect-integration.html. 2 шага:
 *   1. picker: 8 типов провайдеров (Метрика, GA4, Telegram, AmoCRM, ...)
 *   2. config: поля per type (counter_id, token, etc.)
 *
 * Backend POST /api/v1/integrations/{provider}/connect — pending.
 * При submit показываем alert / UiDevCard hint что backend в разработке.
 *
 * Open: modals.open('connect-integration', { providerHint?: string })
 *   providerHint — pre-select провайдера (например из /integrations card).
 * Emit: 'connect-integration' { provider, config } после успешного connect.
 */
import { computed, ref, watch } from 'vue';
import { useModalStore } from '@/stores/modal';
import UiModal from '@/components/ui/UiModal.vue';
import UiButton from '@/components/ui/UiButton.vue';
import UiField from '@/components/ui/UiField.vue';
import UiInput from '@/components/ui/UiInput.vue';
import UiDevCard from '@/components/ui/UiDevCard.vue';

const modals = useModalStore();

type ProviderKey = 'yandex-metrika' | 'google-analytics' | 'telegram-bot' | 'amocrm' | 'bitrix24' | 'mailchimp' | 'sendsay' | 'slack';

interface Provider {
  key: ProviderKey;
  label: string;
  icon: string; // emoji или short svg ref
  desc: string;
  fields: Array<{ name: string; label: string; placeholder?: string; hint?: string; type?: 'text' | 'password' }>;
}

const PROVIDERS: Provider[] = [
  {
    key: 'yandex-metrika',
    label: 'Яндекс.Метрика',
    icon: '📊',
    desc: 'Передавать клики и конверсии в счётчик.',
    fields: [
      { name: 'counter_id', label: 'ID счётчика', placeholder: '12345678', hint: 'Найти в Метрике → Настройки → Общие.' },
      { name: 'oauth_token', label: 'OAuth-токен', type: 'password', hint: 'Получить через oauth.yandex.ru.' },
    ],
  },
  {
    key: 'google-analytics',
    label: 'Google Analytics 4',
    icon: '📈',
    desc: 'Стримить события в GA4 Measurement Protocol.',
    fields: [
      { name: 'measurement_id', label: 'Measurement ID', placeholder: 'G-XXXXXXXXXX' },
      { name: 'api_secret', label: 'API secret', type: 'password' },
    ],
  },
  {
    key: 'telegram-bot',
    label: 'Telegram Bot',
    icon: '✈',
    desc: 'Получать уведомления о кликах / лимитах в чат.',
    fields: [
      { name: 'bot_token', label: 'Bot token', placeholder: '123456:ABC-...', type: 'password', hint: 'Получить у @BotFather.' },
      { name: 'chat_id', label: 'Chat ID', placeholder: '-1001234567890' },
    ],
  },
  {
    key: 'amocrm',
    label: 'amoCRM',
    icon: '🤝',
    desc: 'Создавать сделки на конверсии.',
    fields: [
      { name: 'subdomain', label: 'Поддомен', placeholder: 'mycompany' },
      { name: 'access_token', label: 'Access token', type: 'password' },
    ],
  },
  {
    key: 'bitrix24',
    label: 'Bitrix24',
    icon: '🟢',
    desc: 'Лиды в воронку, входящие webhook.',
    fields: [
      { name: 'webhook_url', label: 'Inbound webhook URL', placeholder: 'https://your.bitrix24.ru/rest/...' },
    ],
  },
  {
    key: 'mailchimp',
    label: 'Mailchimp',
    icon: '📧',
    desc: 'Sync аудиторию по UTM.',
    fields: [
      { name: 'api_key', label: 'API key', type: 'password' },
      { name: 'audience_id', label: 'Audience ID' },
    ],
  },
  {
    key: 'sendsay',
    label: 'Sendsay',
    icon: '📨',
    desc: 'Email-кампании через Sendsay API.',
    fields: [
      { name: 'login', label: 'Логин', placeholder: 'sub:account@example.com' },
      { name: 'password', label: 'Пароль', type: 'password' },
    ],
  },
  {
    key: 'slack',
    label: 'Slack',
    icon: '💬',
    desc: 'Уведомления о фрод-всплесках и лимитах.',
    fields: [
      { name: 'webhook_url', label: 'Incoming webhook URL', placeholder: 'https://hooks.slack.com/services/...' },
    ],
  },
];

const step = ref<'pick' | 'config'>('pick');
const selected = ref<ProviderKey | null>(null);
const values = ref<Record<string, string>>({});
const error = ref<string | null>(null);
const submitting = ref(false);

const selectedProvider = computed<Provider | null>(() => {
  return selected.value ? PROVIDERS.find((p) => p.key === selected.value) || null : null;
});

watch(() => modals.activeKey, (k) => {
  if (k !== 'connect-integration') return;
  step.value = 'pick';
  selected.value = null;
  values.value = {};
  error.value = null;
  const payload = modals.payload as { providerHint?: ProviderKey } | null;
  if (payload?.providerHint) {
    selected.value = payload.providerHint;
    step.value = 'config';
  }
});

function pick(key: ProviderKey): void {
  selected.value = key;
  values.value = {};
  step.value = 'config';
}

function goBack(): void {
  step.value = 'pick';
  selected.value = null;
}

async function submit(): Promise<void> {
  if (!selectedProvider.value) return;
  // Validation: все поля без hint=optional обязательны.
  for (const f of selectedProvider.value.fields) {
    if (!values.value[f.name]?.trim()) {
      error.value = `Заполните: ${f.label}`;
      return;
    }
  }
  submitting.value = true;
  error.value = null;
  try {
    // TODO: backend endpoint POST /api/v1/integrations/{provider}/connect
    // pending. Сейчас alert + close.
    modals.setPending(true);
    await new Promise((r) => setTimeout(r, 400));
    alert(`Backend endpoint /api/v1/integrations/${selectedProvider.value.key}/connect ещё в разработке. Поля прошли валидацию — реальное подключение появится после готовности backend'a.`);
    modals.emit('connect-integration', { provider: selectedProvider.value.key, config: { ...values.value } });
    modals.close();
  } catch (e) {
    error.value = (e as Error).message;
  } finally {
    submitting.value = false;
    modals.setPending(false);
  }
}

function close(): void {
  if (!modals.pending) modals.close();
}
</script>

<template>
  <UiModal
    :title="step === 'pick' ? 'Подключить интеграцию' : `Подключить ${selectedProvider?.label}`"
    :subtitle="step === 'pick' ? 'Выберите провайдера — далее настройте подключение' : selectedProvider?.desc"
    size="md"
    :disable-close="submitting"
    @close="close"
  >
    <!-- Step 1: provider picker -->
    <div v-if="step === 'pick'" class="picker-grid">
      <button
        v-for="p in PROVIDERS"
        :key="p.key"
        type="button"
        class="provider-card"
        @click="pick(p.key)"
      >
        <span class="icon" aria-hidden="true">{{ p.icon }}</span>
        <span class="info">
          <span class="title">{{ p.label }}</span>
          <span class="desc">{{ p.desc }}</span>
        </span>
        <span class="arrow" aria-hidden="true">›</span>
      </button>
    </div>

    <!-- Step 2: config -->
    <form v-else-if="selectedProvider" id="connect-integration-form" class="config" @submit.prevent="submit">
      <UiDevCard
        feature="Backend endpoint в разработке"
        message="POST /api/v1/integrations/{provider}/connect появится отдельной задачей. Сейчас форма валидирует и эмитит данные, но real-connect не происходит."
      />
      <UiField
        v-for="f in selectedProvider.fields"
        :key="f.name"
        :label="f.label"
        :for="`ci-${f.name}`"
        :hint="f.hint"
      >
        <UiInput
          :id="`ci-${f.name}`"
          v-model="values[f.name]"
          :type="f.type === 'password' ? 'password' : 'text'"
          :placeholder="f.placeholder || ''"
          :disabled="submitting"
        />
      </UiField>
      <p v-if="error" class="form-error" role="alert">{{ error }}</p>
    </form>

    <template #footer>
      <UiButton v-if="step === 'config'" variant="ghost" :disabled="submitting" @click="goBack">
        ← Назад
      </UiButton>
      <UiButton v-else variant="ghost" @click="close">Отмена</UiButton>
      <UiButton
        v-if="step === 'config'"
        type="submit"
        form="connect-integration-form"
        :loading="submitting"
      >
        Подключить
      </UiButton>
    </template>
  </UiModal>
</template>

<style scoped>
.picker-grid { display: grid; grid-template-columns: 1fr; gap: 8px; }
@media (min-width: 640px) { .picker-grid { grid-template-columns: 1fr 1fr; } }
.provider-card {
  display: flex; align-items: center; gap: 12px;
  padding: 14px 16px;
  background: var(--color-bg-elev, #0f1620);
  border: 1px solid var(--color-border, #1f2733);
  border-radius: 10px;
  cursor: pointer;
  text-align: left;
  color: inherit;
  font: inherit;
  transition: border-color 0.12s, background 0.12s;
}
.provider-card:hover { border-color: var(--color-accent, #4f8cff); background: var(--color-bg-card, #11161e); }
.icon { font-size: 22px; width: 36px; height: 36px; display: grid; place-items: center; background: rgba(79,140,255,0.08); border-radius: 8px; flex-shrink: 0; }
.info { display: flex; flex-direction: column; gap: 2px; flex: 1; min-width: 0; }
.title { font-size: 14px; font-weight: 600; color: var(--color-fg, #e8edf3); }
.desc { font-size: 12px; color: var(--color-fg-muted, #8b95a5); line-height: 1.45; }
.arrow { color: var(--color-fg-dim, #6b7480); font-size: 18px; flex-shrink: 0; }

.config { display: flex; flex-direction: column; gap: 14px; }
.form-error { font-size: 12.5px; color: var(--color-danger, #ff6b6b); margin: 4px 0 0; }
</style>
